import { defineCommands, type CommandsOf, applyEntityDataChanges, getAllEntities, bumpVersions } from '../../commands/defineCommands';
import type { CanvasStoreContext } from '../../types';
import type { CanvasEntity, GroupData } from '../../entityTypes';
import { getGroupData } from '../../entityTypes';

/**
 * Remove empty groups that lost all their members, cascading up.
 * Only removes groups that were non-empty before (compares with `before` snapshot).
 */
function cleanupNewlyEmptyGroups(
  before: Record<string, CanvasEntity>,
  after: Record<string, CanvasEntity>,
): Record<string, CanvasEntity> {
  const wasNonEmpty = new Set<string>();
  for (const e of Object.values(before)) {
    if (getGroupData(e).memberEntityIds.length > 0) {
      wasNonEmpty.add(e.id);
    }
  }

  let result = after;
  let changed = true;
  while (changed) {
    changed = false;
    const emptyIds = new Set<string>();
    for (const e of Object.values(result)) {
      if (wasNonEmpty.has(e.id) && getGroupData(e).memberEntityIds.length === 0) {
        emptyIds.add(e.id);
      }
    }
    if (emptyIds.size > 0) {
      changed = true;
      for (const e of Object.values(result)) {
        if (!emptyIds.has(e.id)) {
          const gd = getGroupData(e);
          if (gd.memberEntityIds.some(id => emptyIds.has(id)) && gd.memberEntityIds.length > 0) {
            wasNonEmpty.add(e.id);
          }
        }
      }
      const newResult: Record<string, CanvasEntity> = {};
      for (const [id, e] of Object.entries(result)) {
        if (emptyIds.has(id)) continue;
        const gd = getGroupData(e);
        if (gd.memberEntityIds.some(mid => emptyIds.has(mid))) {
          newResult[id] = { ...e, data: { ...e.data, memberEntityIds: gd.memberEntityIds.filter(mid => !emptyIds.has(mid)) } };
        } else {
          newResult[id] = e;
        }
      }
      result = newResult;
    }
  }
  return result;
}

export const groupCommands = defineCommands({
  'group.setMembers': (ctx: CanvasStoreContext, event: { groupId: string; memberEntityIds: string[] }): CanvasStoreContext => {
    const groupEntity = ctx.groups[event.groupId];
    if (!groupEntity) return ctx;

    const allEntities = getAllEntities(ctx);
    const entityMap = new Map(allEntities.map(e => [e.id, e]));
    const dedupedIds = [...new Set(event.memberEntityIds)].filter(id => {
      const e = entityMap.get(id);
      return e && e.layerId === groupEntity.layerId;
    });
    const memberIdSet = new Set(dedupedIds);

    const before = ctx.groups;
    const after: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(before)) {
      if (id === event.groupId) {
        after[id] = { ...e, data: { ...e.data, memberEntityIds: dedupedIds } };
      } else {
        const gd = getGroupData(e);
        const hasAny = gd.memberEntityIds.some(mid => memberIdSet.has(mid));
        if (hasAny) {
          after[id] = { ...e, data: { ...e.data, memberEntityIds: gd.memberEntityIds.filter(mid => !memberIdSet.has(mid)) } };
        } else {
          after[id] = e;
        }
      }
    }

    const cleaned = cleanupNewlyEmptyGroups(before, after);
    const groupsRemoved = Object.keys(cleaned).length !== Object.keys(before).length;
    return { ...ctx, groups: cleaned, ...(groupsRemoved ? bumpVersions(ctx, 'groups') : {}) };
  },

  'group.assignEntities': (ctx: CanvasStoreContext, event: { groupId: string; entityIds: string[] }): CanvasStoreContext => {
    const targetGroup = ctx.groups[event.groupId];
    if (!targetGroup) return ctx;

    const allEntities = getAllEntities(ctx);
    const entityMap = new Map(allEntities.map(e => [e.id, e]));
    const validEntityIds = event.entityIds.filter(id => {
      const e = entityMap.get(id);
      return e && e.layerId === targetGroup.layerId;
    });
    if (validEntityIds.length === 0) return ctx;

    const validIdSet = new Set(validEntityIds);
    const before = ctx.groups;
    const after: Record<string, CanvasEntity> = {};

    for (const [id, e] of Object.entries(before)) {
      if (id === event.groupId) {
        const gd = getGroupData(e);
        const existing = new Set(gd.memberEntityIds);
        const toAdd = validEntityIds.filter(vid => !existing.has(vid));
        after[id] = toAdd.length > 0
          ? { ...e, data: { ...e.data, memberEntityIds: [...gd.memberEntityIds, ...toAdd] } }
          : e;
      } else {
        const gd = getGroupData(e);
        const hasAny = gd.memberEntityIds.some(mid => validIdSet.has(mid));
        if (hasAny) {
          after[id] = { ...e, data: { ...e.data, memberEntityIds: gd.memberEntityIds.filter(mid => !validIdSet.has(mid)) } };
        } else {
          after[id] = e;
        }
      }
    }

    const cleaned = cleanupNewlyEmptyGroups(before, after);
    const groupsRemoved = Object.keys(cleaned).length !== Object.keys(before).length;
    return { ...ctx, groups: cleaned, ...(groupsRemoved ? bumpVersions(ctx, 'groups') : {}) };
  },

  'group.createFromSelection': (ctx: CanvasStoreContext, event: {
    groupId: string;
    selectedEntityIds: string[];
    enteredGroupId: string | null;
    layerId: string;
  }): CanvasStoreContext => {
    const allEntities = getAllEntities(ctx);
    const groups = ctx.groups;

    const entityMap = new Map(allEntities.map(e => [e.id, e]));
    const validSelectedIds = event.selectedEntityIds.filter(id => {
      const e = entityMap.get(id);
      return e && e.layerId === event.layerId;
    });
    if (validSelectedIds.length === 0) return ctx;

    const selectedSet = new Set(validSelectedIds);

    const selectedGroups: CanvasEntity[] = [];
    for (const e of Object.values(groups)) {
      if (selectedSet.has(e.id)) {
        selectedGroups.push(e);
      }
    }

    const explicitChildGroupIds = event.enteredGroupId
      ? selectedGroups.filter(g => g.id !== event.enteredGroupId).map(g => g.id)
      : selectedGroups.map(g => g.id);

    const explicitSet = new Set(explicitChildGroupIds);
    const autoChildGroupIds: string[] = [];
    for (const e of Object.values(groups)) {
      if (e.id === event.enteredGroupId) continue;
      if (explicitSet.has(e.id)) continue;
      const gd = getGroupData(e);
      if (gd.memberEntityIds.length === 0) continue;
      if (gd.memberEntityIds.every(id => selectedSet.has(id))) {
        autoChildGroupIds.push(e.id);
      }
    }
    const allChildGroupIds = [...explicitChildGroupIds, ...autoChildGroupIds];

    const childMemberIds = new Set<string>();
    for (const cid of allChildGroupIds) {
      const cg = groups[cid];
      if (cg) {
        const gd = getGroupData(cg);
        for (const mid of gd.memberEntityIds) childMemberIds.add(mid);
      }
    }

    const parentMemberEntityIds = validSelectedIds.filter(id => !childMemberIds.has(id));
    const newGroupMemberIds = [
      ...parentMemberEntityIds.filter(id => !allChildGroupIds.includes(id)),
      ...allChildGroupIds,
    ];

    const childGroupIdSet = new Set(allChildGroupIds);
    const updatedGroups: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(groups)) {
      if (childGroupIdSet.has(id)) { updatedGroups[id] = e; continue; }
      if (id === event.enteredGroupId) { updatedGroups[id] = e; continue; }
      const gd = getGroupData(e);
      const remaining = gd.memberEntityIds.filter(mid => !selectedSet.has(mid));
      if (remaining.length < gd.memberEntityIds.length) {
        updatedGroups[id] = { ...e, data: { ...e.data, memberEntityIds: remaining } };
      } else {
        updatedGroups[id] = e;
      }
    }

    const maxZ = allEntities.reduce((m, e) => Math.max(m, e.zIndex ?? 0), 0);
    const newGroup: CanvasEntity = {
      id: event.groupId,
      kind: 'group',
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      layerId: event.layerId,
      connectable: false,
      zIndex: maxZ + 1,
      data: {
        label: `Group ${Object.keys(groups).length + 1}`,
        backgroundColor: 'rgba(147, 197, 253, 0.2)',
        collapsed: false,
        memberEntityIds: newGroupMemberIds,
        displayMode: 'invisible',
      } satisfies GroupData,
    };
    updatedGroups[event.groupId] = newGroup;

    if (event.enteredGroupId && updatedGroups[event.enteredGroupId]) {
      const parentG = updatedGroups[event.enteredGroupId];
      const gd = getGroupData(parentG);
      updatedGroups[event.enteredGroupId] = {
        ...parentG,
        data: {
          ...parentG.data,
          memberEntityIds: [
            ...gd.memberEntityIds.filter(id => !selectedSet.has(id)),
            event.groupId,
          ],
        },
      };
    }

    return { ...ctx, groups: updatedGroups, ...bumpVersions(ctx, 'groups') };
  },

  'group.toggleCollapse': (ctx: CanvasStoreContext, event: { groupId: string; collapsedX?: number; collapsedY?: number }): CanvasStoreContext => {
    const e = ctx.groups[event.groupId];
    if (!e) return ctx;
    const gd = getGroupData(e);
    const nowCollapsed = !gd.collapsed;
    return {
      ...ctx,
      groups: {
        ...ctx.groups,
        [event.groupId]: {
          ...e,
          data: {
            ...e.data,
            collapsed: nowCollapsed,
            ...(nowCollapsed && event.collapsedX !== undefined ? { collapsedX: event.collapsedX } : {}),
            ...(nowCollapsed && event.collapsedY !== undefined ? { collapsedY: event.collapsedY } : {}),
          },
        },
      },
    };
  },

  'group.setLayer': (ctx: CanvasStoreContext, event: { groupId: string; layerId: string }): CanvasStoreContext => {
    const allEntities = getAllEntities(ctx);
    const entityMap = new Map(allEntities.map(e => [e.id, e]));

    const descendantIds = new Set<string>();
    function collectDescendants(gid: string) {
      const g = entityMap.get(gid);
      if (!g || g.kind !== 'group') return;
      const gd = getGroupData(g);
      for (const mid of gd.memberEntityIds) {
        descendantIds.add(mid);
        collectDescendants(mid);
      }
    }
    collectDescendants(event.groupId);

    const newNodes: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(ctx.nodes)) {
      newNodes[id] = descendantIds.has(id) ? { ...e, layerId: event.layerId } : e;
    }

    const newGroups: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(ctx.groups)) {
      newGroups[id] = (id === event.groupId || descendantIds.has(id)) ? { ...e, layerId: event.layerId } : e;
    }

    return { ...ctx, nodes: newNodes, groups: newGroups };
  },

  'group.updateData': (ctx: CanvasStoreContext, event: {
    groupId: string; dataChanges: Partial<GroupData>;
  }): CanvasStoreContext => applyEntityDataChanges(ctx, event.groupId, event.dataChanges),

  'group.batchMoveCollapsed': (ctx: CanvasStoreContext, event: { moves: Array<{ groupId: string; collapsedX: number; collapsedY: number }> }): CanvasStoreContext => {
    if (event.moves.length === 0) return ctx;
    const newGroups = { ...ctx.groups };
    for (const move of event.moves) {
      const e = newGroups[move.groupId];
      if (e) newGroups[move.groupId] = { ...e, data: { ...e.data, collapsedX: move.collapsedX, collapsedY: move.collapsedY } };
    }
    return { ...ctx, groups: newGroups };
  },

  'group.reorderMembers': (ctx: CanvasStoreContext, event: { groupId: string; memberEntityIds: string[] }): CanvasStoreContext => {
    const e = ctx.groups[event.groupId];
    if (!e) return ctx;
    const gd = getGroupData(e);
    const reorderSet = new Set(event.memberEntityIds);
    const nonReordered = gd.memberEntityIds.filter(id => !reorderSet.has(id));
    return {
      ...ctx,
      groups: {
        ...ctx.groups,
        [event.groupId]: { ...e, data: { ...e.data, memberEntityIds: [...event.memberEntityIds, ...nonReordered] } },
      },
    };
  },
});

export type GroupCommand = CommandsOf<typeof groupCommands>;
